<template>
  <transition
    name="zm-zoom-in-top"
    @after-enter="handleEnter"
    @after-leave="handleLeave">
    <div
      v-show="visible"
      class="zm-time-range-picker zm-picker-panel zm-popper"
      role="application"
      aria-label="Calendar view time range picker"
      :class="popperClass">
      <div class="zm-time-range-picker__content">
        <div class="zm-time-range-picker__cell">
          <div class="zm-time-range-picker__header">{{ t('datepicker.startTime') }}</div>
          <div
            :class="{ 'has-seconds': showSeconds, 'is-arrow': arrowControl }"
            class="zm-time-range-picker__body zm-time-panel__content">
            <time-spinner
              ref="minSpinner"
              :show-seconds="showSeconds"
              :am-pm-mode="amPmMode"
              @change="handleMinChange"
              :arrow-control="arrowControl"
              @select-range="setMinSelectionRange"
              :aria-label-title="t('datepicker.startTime')"
              :date="minDate">
            </time-spinner>
          </div>
        </div>
        <div class="zm-time-range-picker__cell">
          <div class="zm-time-range-picker__header">{{ t('datepicker.endTime') }}</div>
          <div
            :class="{ 'has-seconds': showSeconds, 'is-arrow': arrowControl }"
            class="zm-time-range-picker__body zm-time-panel__content">
            <time-spinner
              ref="maxSpinner"
              :show-seconds="showSeconds"
              :am-pm-mode="amPmMode"
              @change="handleMaxChange"
              :arrow-control="arrowControl"
              @select-range="setMaxSelectionRange"
              :aria-label-title="t('datepicker.endTime')"
              :date="maxDate">
            </time-spinner>
          </div>
        </div>
      </div>
      <div class="zm-time-panel__footer">
        <button
          type="button"
          class="zm-time-panel__btn cancel"
          @click="handleCancel()">{{ t('datepicker.cancel') }}
        </button>
        <button
          type="button"
          class="zm-time-panel__btn confirm"
          @click="handleConfirm()"
          :disabled="btnDisabled">{{ t('datepicker.confirm') }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
  import {
    parseDate,
    limitTimeRange,
    modifyDate,
    clearMilliseconds,
    timeWithinRange
  } from '../util';
  import Locale from '../../../mixins/locale';
  import TimeSpinner from '../basic/time-spinner';
  import AriaDialog from '../../../utils/aria-dialog';
  const minTimeOfDay = function(date) {
    const MIN_TIME = parseDate('00:00:00', 'HH:mm:ss');
    return modifyDate(MIN_TIME, date.getFullYear(), date.getMonth(), date.getDate());
  };

  const maxTimeOfDay = function(date) {
    const MAX_TIME = parseDate('23:59:59', 'HH:mm:ss');
    return modifyDate(MAX_TIME, date.getFullYear(), date.getMonth(), date.getDate());
  };

  // increase time by amount of milliseconds, but within the range of day
  const advanceTime = function(date, amount) {
    return new Date(Math.min(date.getTime() + amount, maxTimeOfDay(date).getTime()));
  };

  export default {
    mixins: [Locale],

    components: {TimeSpinner},

    computed: {
      showSeconds() {
        return (this.format || '').indexOf('ss') !== -1;
      },

      offset() {
        return this.showSeconds ? 11 : 8;
      },

      spinner() {
        return this.selectionRange[0] < this.offset ? this.$refs.minSpinner : this.$refs.maxSpinner;
      },

      btnDisabled() {
        return this.minDate.getTime() > this.maxDate.getTime();
      },
      amPmMode() {
        if ((this.format || '').indexOf('A') !== -1) return 'A';
        if ((this.format || '').indexOf('a') !== -1) return 'a';
        return '';
      }
    },

    data() {
      return {
        popperClass: '',
        minDate: new Date(),
        maxDate: new Date(),
        value: [],
        oldValue: [new Date(), new Date()],
        defaultValue: null,
        format: 'HH:mm:ss',
        visible: false,
        selectionRange: [0, 2],
        arrowControl: true
      };
    },

    watch: {
      value(value) {
        if (Array.isArray(value)) {
          this.minDate = new Date(value[0]);
          this.maxDate = new Date(value[1]);
        } else {
          if (Array.isArray(this.defaultValue)) {
            this.minDate = new Date(this.defaultValue[0]);
            this.maxDate = new Date(this.defaultValue[1]);
          } else if (this.defaultValue) {
            this.minDate = new Date(this.defaultValue);
            this.maxDate = advanceTime(new Date(this.defaultValue), 60 * 60 * 1000);
          } else {
            this.minDate = new Date();
            this.maxDate = advanceTime(new Date(), 60 * 60 * 1000);
          }
        }
      },

      visible(val) {
        if (val) {
          this.oldValue = this.value;
          this.$nextTick(() => this.$refs.minSpinner.emitSelectRange('hours'));
        }
      }
    },

    methods: {
      handleClear() {
        this.$emit('pick', null);
      },

      handleCancel() {
        this.$emit('pick', this.oldValue);
      },

      handleMinChange(date) {
        this.minDate = clearMilliseconds(date);
        this.handleChange();
      },

      handleMaxChange(date) {
        this.maxDate = clearMilliseconds(date);
        this.handleChange();
      },

      handleChange() {
        if (this.isValidValue([this.minDate, this.maxDate])) {
          this.$refs.minSpinner.selectableRange = [[minTimeOfDay(this.minDate), this.maxDate]];
          this.$refs.maxSpinner.selectableRange = [[this.minDate, maxTimeOfDay(this.maxDate)]];
          this.$emit('pick', [this.minDate, this.maxDate], true);
        }
      },

      setMinSelectionRange(start, end) {
        this.$emit('select-range', start, end, 'min');
        this.selectionRange = [start, end];
      },

      setMaxSelectionRange(start, end) {
        this.$emit('select-range', start, end, 'max');
        this.selectionRange = [start + this.offset, end + this.offset];
      },

      handleConfirm(visible = false) {
        const minSelectableRange = this.$refs.minSpinner.selectableRange;
        const maxSelectableRange = this.$refs.maxSpinner.selectableRange;

        this.minDate = limitTimeRange(this.minDate, minSelectableRange, this.format);
        this.maxDate = limitTimeRange(this.maxDate, maxSelectableRange, this.format);

        this.$emit('pick', [this.minDate, this.maxDate], visible);
      },

      adjustSpinners() {
        this.$refs.minSpinner.adjustSpinners();
        this.$refs.maxSpinner.adjustSpinners();
      },

      changeSelectionRange(step) {
        const list = this.showSeconds ? [0, 3, 6, 11, 14, 17] : [0, 3, 8, 11];
        const mapping = ['hours', 'minutes'].concat(this.showSeconds ? ['seconds'] : []);
        const index = list.indexOf(this.selectionRange[0]);
        const next = (index + step + list.length) % list.length;
        const half = list.length / 2;
        if (next < half) {
          this.$refs.minSpinner.emitSelectRange(mapping[next]);
        } else {
          this.$refs.maxSpinner.emitSelectRange(mapping[next - half]);
        }
      },

      isValidValue(date) {
        return Array.isArray(date) &&
          timeWithinRange(this.minDate, this.$refs.minSpinner.selectableRange) &&
          timeWithinRange(this.maxDate, this.$refs.maxSpinner.selectableRange);
      },

      handleKeydown(event) {
        const keyCode = event.keyCode;
        const mapping = {38: -1, 40: 1, 37: -1, 39: 1};

        // Left or Right
        if (keyCode === 37 || keyCode === 39) {
          const step = mapping[keyCode];
          this.changeSelectionRange(step);
          event.preventDefault();
          return;
        }

        // Up or Down
        if (keyCode === 38 || keyCode === 40) {
          const step = mapping[keyCode];
          this.spinner.scrollDown(step);
          event.preventDefault();
          return;
        }
      },
      keyEventHandler(event) {
        if (event.keyCode === 27) {
          this.$emit('escClose');
          this.handleLeave();
          this.visible = false;
        }
      },
      handleEnter() {
        document.body.addEventListener('keydown', this.keyEventHandler);
        const focusAfterClosed = document.activeElement;
        this.$nextTick(() => {
          if (!this.ariaDialog) {
            let firstFocusEl = this.$el.querySelector('.btn-firstFocus');
            this.ariaDialog = new AriaDialog(this.$el, focusAfterClosed, firstFocusEl);
          }
        });
      },
      handleLeave() {
        this.$emit('dodestroy');
        this.flagLeave = true;
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
        document.body.removeEventListener('keydown', this.keyEventHandler);
      }
    }
  };
</script>

<style lang="scss">
  @import "../../../scss/common/var";
  @import "../../../scss/mixins/mixins";

  @include b(time-range-picker) {
    width: 354px;
    overflow: visible;

    @include e(content) {
      position: relative;
      text-align: center;
      padding: 10px;
    }

    @include e(cell) {
      box-sizing: border-box;
      margin: 0;
      padding: 4px 7px 7px;
      width: 50%;
      display: inline-block;
    }

    @include e(header) {
      margin-bottom: 5px;
      text-align: center;
      font-size: 14px;
    }

    @include e(body) {
      border-radius:2px;
      border: 1px solid $--datepicker-border-color;
    }

  }
  @include b(time-panel) {
    @include e(footer) {
      border-top: 1px solid $--datepicker-inner-border-color;
      padding: 4px;
      height: 36px;
      line-height: 25px;
      text-align: right;
      box-sizing: border-box;
    }
    @include e(btn) {
      border: none;
      line-height: 24px;
      padding: 0 5px;
      margin: 0 5px;
      cursor: pointer;
      background-color: transparent;
      outline: none;
      font-size: 12px;
      color: $--color-text-primary;
      &:hover,
      &:focus {
        color: $--color-primary;
      }
      &.confirm {
        font-weight: 800;
        color: $--datepicker-active-color;
        border-radius: $--button-small-border-radius;
        &:hover,
        &:focus {
          background-color: $--color-background-secondary-hover;
        }
      }
    }
  }
</style>
